import React, { useState } from 'react';
import { Form, Input, Button, Typography, Card } from 'antd';
import { LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { AppDispatch, useAppSelector } from '../../redux/store';
import { selfUpdatePasswordByUser } from '../../redux/slices/authSlice';
import { toast } from 'react-hot-toast';

const { Title, Text } = Typography;

const ForceChangePasswordModal: React.FC = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);

  const userId = useAppSelector((state) => state.auth.user?.user_id) ||
    JSON.parse(localStorage.getItem('userDetails') || '{}')?.user_id;

  const handleSubmit = async (values: any) => {
    if (!userId) {
      toast.error('Session error. Please log in again.');
      return;
    }

    if (values.newPassword !== values.confirmPassword) {
      toast.error('New password and confirm password do not match');
      return;
    }

    setLoading(true);
    try {
      await dispatch(selfUpdatePasswordByUser({
        userId: Number(userId),
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      })).unwrap();

      toast.success('Password updated successfully! Welcome to your dashboard.');
    } catch (err: any) {
      toast.error(typeof err === 'string' ? err : 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(243, 244, 246, 0.6)', position: 'fixed', top: 0, left: 0, zIndex: 9999 }}>
      <Card style={{ width: 450, borderRadius: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24, marginTop: 12 }}>
          <SafetyCertificateOutlined style={{ fontSize: 48, color: '#00BAA7', marginBottom: 12 }} />
          <Title level={3} style={{ margin: 0 }}>Action Required</Title>
          <Text type="secondary">You are using a default password. Please choose a new password to continue.</Text>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSubmit} size="large">
          <Form.Item name="currentPassword" label="Current Default Password" rules={[{ required: true, message: 'Please enter current password' }]}>
            <Input.Password prefix={<LockOutlined style={{ color: '#9ca3af' }} />} placeholder="Current Password" />
          </Form.Item>

          <Form.Item name="newPassword" label="New Password" rules={[{ required: true, min: 8, message: 'Minimum 8 characters' }]}>
            <Input.Password prefix={<LockOutlined style={{ color: '#9ca3af' }} />} placeholder="New Password" />
          </Form.Item>

          <Form.Item name="confirmPassword" label="Confirm New Password" dependencies={['newPassword']} rules={[
            { required: true, message: 'Confirm your password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}>
            <Input.Password prefix={<LockOutlined style={{ color: '#9ca3af' }} />} placeholder="Confirm New Password" />
          </Form.Item>

          <Button type="primary" htmlType="submit" loading={loading} block style={{ background: '#00BAA7', borderColor: '#00BAA7', borderRadius: 6, marginTop: 12 }}>
            Update Password
          </Button>
        </Form>
      </Card>
    </div>
  );
};

export default ForceChangePasswordModal;
